import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "../../core/Dialog/Dialog";
import { Button } from "../../core/Button/Button";
import { Toggle } from "../../core/Toggle/Toggle";
import { Typography } from "../../core/Typography/Typography";
import { SettingRow } from "../SettingRow";
import { TypographyVariant } from "../../../constants/typography";
import { IS_CREATION_LOCK_SUPPORTED } from "../../../models/profile";
import { useStyles } from "./AccountPinLocksPopup.style";

interface IAccountPinLocksPopupProps {
  open: boolean;
  profileLock: boolean;
  creationLock: boolean;
  focusUpdateButton?: boolean;
  onUpdateAccountPin: () => void;
  onToggleProfileLock: (isLocked: boolean) => void;
  onToggleCreationLock: (isLocked: boolean) => void;
  onSave: () => void;
  onClose: () => void;
}

export const AccountPinLocksPopup: FC<IAccountPinLocksPopupProps> = ({
  open,
  profileLock,
  creationLock,
  focusUpdateButton,
  onUpdateAccountPin,
  onToggleProfileLock,
  onToggleCreationLock,
  onSave,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      title={t("AccountPinLocks_Account PIN and Locks")}
      data-dialog-nav="rows"
      onClose={onClose}
      mainAction={{
        text: t("AccountPinLocks_Save"),
        onClick: onSave,
        "data-gamepad": focusUpdateButton
          ? "account-locks-save"
          : "default-selected",
      }}
      secondAction={{
        text: t("AccountPinLocks_Cancel"),
        onClick: onClose,
        "data-gamepad": "account-locks-cancel",
      }}
    >
      <div className={classes.content}>
        <Typography
          variant={TypographyVariant.HeadingH5Bold}
          className={classes.title}
        >
          {t("AccountPinLocks_Account PIN and Locks")}
        </Typography>

        <SettingRow
          title={t("AccountPinLocks_Account PIN")}
          description={t("AccountPinLocks_Update your current PIN code")}
          control={
            <Button
              size="small"
              className={classes.rowButton}
              onClick={onUpdateAccountPin}
              data-gamepad={
                focusUpdateButton
                  ? "default-selected"
                  : "account-locks-update-pin"
              }
            >
              {t("AccountPinLocks_Update")}
            </Button>
          }
        />

        <SettingRow
          title={t("AccountPinLocks_Profile Lock")}
          description={t(
            "AccountPinLocks_Require a PIN to enter or manage this profile",
          )}
          control={
            <Toggle
              checked={profileLock}
              onChange={(checked) => onToggleProfileLock(!!checked)}
              data-gamepad="account-locks-profile-lock"
              aria-label={t("AccountPinLocks_Profile Lock")}
              data-selected-class={classes.toggleSelected}
            />
          }
        />

        {IS_CREATION_LOCK_SUPPORTED && (
          <SettingRow
            title={t("AccountPinLocks_Creation Lock")}
            description={t(
              "AccountPinLocks_Require PIN to create new profiles",
            )}
            control={
              <Toggle
                checked={creationLock}
                onChange={(checked) => onToggleCreationLock(!!checked)}
                data-gamepad="account-locks-creation-lock"
                aria-label={t("AccountPinLocks_Creation Lock")}
                data-selected-class={classes.toggleSelected}
              />
            }
          />
        )}
      </div>
    </Dialog>
  );
};
